"use client";

import * as React from "react";
import { useDropzone } from "react-dropzone";
import { FileText, UploadCloud, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface FileUploaderV2File {
  file: File;
  name: string;
}

export interface FileUploaderV2Props {
  value?: FileUploaderV2File | null;
  onChange?: (value: FileUploaderV2File | null) => void;
  /** Extensiones separadas por coma, p. ej. `.pdf,.png,.jpg`. */
  accept?: string;
  /** Texto de ayuda bajo el llamado a la acción (formatos / tamaño). */
  hint?: string;
  error?: string;
  disabled?: boolean;
  /** Texto principal del dropzone vacío. */
  title?: string;
  "aria-label"?: string;
}

const DEFAULT_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";

function parseAccept(accept: string): string[] {
  return accept
    .split(",")
    .map((e) => e.trim())
    .filter(Boolean);
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Document-oriented file uploader for the v2 design language. Unlike
 * ImageUploaderV2 (a square photo picker), this is a full-width drag-and-drop
 * zone meant for certificates / PDFs / documents, with a selected-file chip
 * instead of an image preview.
 */
export function FileUploaderV2({
  value = null,
  onChange,
  accept = DEFAULT_ACCEPT,
  hint,
  error,
  disabled = false,
  title,
  "aria-label": ariaLabel = "Subir archivo"
}: FileUploaderV2Props) {
  const onDrop = React.useCallback(
    (accepted: File[]) => {
      const file = accepted[accepted.length - 1];
      if (!file || !onChange) return;
      onChange({ file, name: file.name });
    },
    [onChange]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } =
    useDropzone({
      onDrop,
      accept: parseAccept(accept),
      multiple: false,
      disabled,
      noClick: Boolean(value?.file)
    });

  if (value?.file) {
    return (
      <div
        className={cn(
          "flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900",
          error && "border-rose-400 dark:border-rose-500"
        )}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FileText className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
            {value.name}
          </p>
          {value.file.size > 0 && (
            <p className="text-xs text-slate-500 dark:text-zinc-400">
              {formatSize(value.file.size)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={open}
          disabled={disabled}
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-500/10"
        >
          Reemplazar
        </button>
        <button
          type="button"
          aria-label="Quitar archivo"
          onClick={() => onChange?.(null)}
          disabled={disabled}
          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
        >
          <X className="h-4 w-4" />
        </button>
        <input {...getInputProps()} />
      </div>
    );
  }

  return (
    <>
      <div
        {...getRootProps()}
        aria-label={ariaLabel}
        className={cn(
          "group flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors",
          "border-slate-200 bg-slate-50/60 hover:border-indigo-300 hover:bg-indigo-50/40",
          "dark:border-zinc-700 dark:bg-zinc-900/40 dark:hover:border-indigo-500/50 dark:hover:bg-indigo-500/5",
          isDragActive &&
            "border-indigo-400 bg-indigo-50/70 dark:border-indigo-400 dark:bg-indigo-500/10",
          isDragReject && "border-rose-400 bg-rose-50/60 dark:border-rose-500",
          error && "border-rose-400 dark:border-rose-500",
          disabled && "cursor-not-allowed opacity-60"
        )}
      >
        <input {...getInputProps()} />
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200/70 transition-transform group-hover:scale-105 dark:bg-zinc-800 dark:text-indigo-300 dark:ring-zinc-700">
          <UploadCloud className="h-6 w-6" />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-700 dark:text-zinc-100">
            {isDragActive
              ? "Suelta el archivo aquí"
              : title ?? "Arrastra y suelta tu archivo"}
          </p>
          <p className="mt-0.5 text-sm text-slate-500 dark:text-zinc-400">
            o{" "}
            <span className="font-medium text-indigo-600 dark:text-indigo-300">
              haz clic para seleccionar
            </span>
          </p>
          {hint && (
            <p className="mt-1 text-xs text-slate-400 dark:text-zinc-500">
              {hint}
            </p>
          )}
        </div>
      </div>
      {error && (
        <p className="mt-1 text-xs font-medium text-rose-500">{error}</p>
      )}
    </>
  );
}
